import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Label, Badge } from 'flowbite-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { request } from '../../utils/remote';
import { useGrades } from '../../react-query/queries';
import { GoogleGenAI } from '@google/genai';
import DOMPurify from 'dompurify';

interface AIQuestionReviewDialogProps {
    show: boolean;
    onClose: () => void;
    questions: any[];
    sharedNotes?: any[];
    examId: string;
    onSaveComplete: () => void;
}

const parseModelResponse = (r: any): string | null => {
    if (!r) return null
    if (typeof r === 'string') return r
    if (typeof r.text === 'string') return r.text
    if (Array.isArray(r.candidates) && r.candidates.length && typeof r.candidates[0].content === 'string') return r.candidates[0].content
    if (Array.isArray(r.outputs) && r.outputs.length) {
        const out0 = r.outputs[0]
        if (typeof out0 === 'string') return out0
        if (out0?.content && typeof out0.content === 'object') {
            if (typeof out0.content?.text === 'string') return out0.content.text
            if (Array.isArray(out0.content) && out0.content.length && typeof out0.content[0]?.text === 'string') return out0.content[0].text
        }
    }
    return null
}

const AIQuestionReviewDialog: React.FC<AIQuestionReviewDialogProps> = ({ show, onClose, questions, sharedNotes, examId, onSaveComplete }) => {
    const { token } = useAuth();
    const [localQuestions, setLocalQuestions] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isAssigningChapters, setIsAssigningChapters] = useState(false);

    const [showGradeSelect, setShowGradeSelect] = useState(false);
    const { data: gradesData } = useGrades(token);
    const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([]);

    useEffect(() => {
        if (show) {
            setLocalQuestions(questions.map(q => ({ ...q, chapter_id: null, chapter_name: null })));
        }
    }, [show, questions]);

    const getUserKeyOrPrompt = async (): Promise<string | null> => {
        try {
            const kd = await request('/api/user/gemini-key/', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
            return kd?.has_key && kd?.api_key ? kd.api_key : null
        } catch (e) {
            console.warn('Could not fetch gemini key', e)
            return null
        }
    }

    const handleAssignChapters = async () => {
        if (selectedGradeIds.length === 0) {
            alert('Please select at least one grade');
            return;
        }

        const geminiKey = await getUserKeyOrPrompt();
        if (!geminiKey) {
            alert('Please set your Gemini API key in your profile first.');
            return;
        }

        setIsAssigningChapters(true);
        try {
            const catResp = await request(`/api/school/exam-detail/${examId}/assign-chapters-ai/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ grade_ids: selectedGradeIds })
            });

            if (!catResp || !catResp.catalog) {
                throw new Error('Failed to fetch chapter catalog');
            }

            const catalog = catResp.catalog;
            const ai = new GoogleGenAI({ apiKey: geminiKey.trim() });

            const systemPrompt = (
                "You are an educational assistant. You are given a list of questions and a catalog of chapters. " +
                "For each question, identify the most relevant chapter from the catalog. " +
                "Return a JSON array of objects, each with 'question_idx' and 'chapter_id'. " +
                "If no chapter matches well, return null for chapter_id. Respond with the JSON array only, no other text."
            );

            const inputData = localQuestions.map((q, i) => ({
                idx: i,
                text: q.question || q.ques,
                choices: [q.choice_a, q.choice_b, q.choice_c, q.choice_d]
            }));

            const userPrompt = `Catalog: ${JSON.stringify(catalog)}\n\nQuestions: ${JSON.stringify(inputData)}`;

            const result = await ai.models.generateContent({model: 'gemini-2.5-flash', contents: `${systemPrompt}\n\n${userPrompt}` })

            let assignments: any[] = [];
            try {
                const responseText = parseModelResponse(result);
                if (responseText) {
                    const jsonMatch = responseText.match(/(\[\s*\{.*\}\s*\])/s);
                    const cleanJson = jsonMatch ? jsonMatch[1] : responseText;
                    assignments = JSON.parse(cleanJson);
                }
            } catch (e) {
                console.error('Failed to parse AI response', e);
                throw new Error('AI returned invalid JSON');
            }

            const updated = [...localQuestions];
            assignments.forEach((as: any) => {
                if (as.question_idx !== undefined && updated[as.question_idx]) {
                    updated[as.question_idx].chapter_id = as.chapter_id;
                    const chap = catalog.find((c: any) => c.id === as.chapter_id);
                    if (chap) updated[as.question_idx].chapter_name = chap.name;
                }
            });

            setLocalQuestions(updated);
            setShowGradeSelect(false);
            alert('Chapters assigned successfully by AI!');

        } catch (error) {
            console.error('Failed to assign chapters', error);
            alert('Failed to assign chapters using AI: ' + (error instanceof Error ? error.message : String(error)));
        } finally {
            setIsAssigningChapters(false);
        }
    };

    const handleSaveAll = async () => {
        setIsSaving(true);
        try {
            const noteMapping: Record<number, number> = {};
            if (sharedNotes && sharedNotes.length > 0) {
                for (let i = 0; i < sharedNotes.length; i++) {
                    const note = sharedNotes[i];
                    try {
                        const noteResp = await request('/api/shared-notes/', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(note)
                        });
                        if (noteResp && noteResp.id) {
                            noteMapping[i] = noteResp.id;
                        }
                    } catch (e) {
                        console.error('Failed to create shared note', note, e);
                    }
                }
            }

            const questionsWithNotes = localQuestions.map(q => {
                const copy = { ...q };
                if (q.note_idx !== undefined && noteMapping[q.note_idx] !== undefined) {
                    copy.shared_note = noteMapping[q.note_idx];
                }
                return copy;
            });

            await request(`/api/school/exam-detail/${examId}/add-other-questions/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ questions: questionsWithNotes })
            });

            onSaveComplete();
            alert('All questions saved successfully!');
        } catch (error) {
            console.error('Failed to save questions', error);
            alert('Failed to save questions');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleGrade = (id: number) => {
        setSelectedGradeIds(prev =>
            prev.includes(id) ? prev.filter(gid => gid !== id) : [...prev, id]
        );
    };

    const renderLatex = (html: string) => {
        if (!html) return '';
        return DOMPurify.sanitize(html);
    };

    return (
        <Modal show={show} onClose={onClose} size="6xl" className="z-[11000]" style={{ zIndex: 11000 }}> 
            <Modal.Header>Review Extracted Questions</Modal.Header>
            <Modal.Body>
                <div className="flex justify-between items-center mb-6">
                    <p className="text-gray-600">Review the questions extracted by AI. You can also generate chapters for them.</p>
                    <Button color="purple" onClick={() => setShowGradeSelect(true)}>Generate Chapter</Button>
                </div>

                <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                    {localQuestions.map((q, idx) => (
                        <div key={idx} className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-800 relative">
                            <div className="absolute top-2 right-2">
                                {q.chapter_id ? (
                                    <Badge color="success">{q.chapter_name || `Chapter ID: ${q.chapter_id}`}</Badge>
                                ) : (
                                    <Badge color="warning">No Chapter</Badge>
                                )}
                            </div>
                            <div className="font-bold mb-2 flex justify-between items-center">
                                <span>Question {idx + 1}</span>
                                {q.note_idx !== undefined && sharedNotes && sharedNotes[q.note_idx] && (
                                    <Badge color="indigo">Shared Note: {sharedNotes[q.note_idx].title}</Badge>
                                )}
                            </div>
                            <div className="prose prose-sm dark:prose-invert mb-4" dangerouslySetInnerHTML={{ __html: renderLatex(q.question || q.ques) }} />
                            
                            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">A:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_a) }} />
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">B:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_b) }} />
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">C:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_c) }} />
                                </div>
                                <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                                    <span className="font-semibold mr-2">D:</span>
                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.choice_d) }} />
                                </div>
                            </div>

                            <div className="flex gap-4 items-center">
                                <Badge color="info">Answer: {q.answer || q.ans}</Badge>
                            </div>

                            {q.explanation && (
                                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-100 dark:border-blue-800">
                                    <div className="text-xs font-bold text-blue-800 dark:text-blue-300 mb-1">AI Explanation</div>
                                    <div className="text-sm italic" dangerouslySetInnerHTML={{ __html: renderLatex(q.explanation) }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <Modal show={showGradeSelect} onClose={() => setShowGradeSelect(false)} size="md" className="z-[12000]" style={{ zIndex: 12000 }}>
                    <Modal.Header>Select Grades for Chapter Assignment</Modal.Header>
                    <Modal.Body>
                        <div className="space-y-4">
                            <p className="text-sm text-gray-500">AI will look for chapters from these grades to assign to your questions.</p>
                            <div className="max-h-60 overflow-y-auto border rounded p-3">
                                {gradesData?.grades?.map((g: any) => (
                                    <div key={g.id} className="flex items-center gap-2 py-2">
                                        <Checkbox id={`grade-assign-${g.id}`} checked={selectedGradeIds.includes(g.id)} onChange={() => toggleGrade(g.id)} />
                                        <Label htmlFor={`grade-assign-${g.id}`}>{g.grade_name}</Label>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <Button color="gray" onClick={() => setShowGradeSelect(false)}>Cancel</Button>
                                <Button onClick={handleAssignChapters} isProcessing={isAssigningChapters}>Assign Chapters</Button>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex justify-end w-full gap-4">
                    <Button color="gray" onClick={onClose}>Cancel</Button>
                    <Button color="success" onClick={handleSaveAll} isProcessing={isSaving} disabled={localQuestions.length === 0}>
                        Save All Questions
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default AIQuestionReviewDialog;
